interface DosageWithSolirisProps {
  cycles: number;
  vialNumber: number;
  loadingDose: number;
  maintenanceDose: number;
  weeks: string;
  weight: string;
}

export const DosageWithSoliris = ({
  cycles,
  vialNumber,
  loadingDose,
  maintenanceDose,
  weeks,
  weight,
}: DosageWithSolirisProps) => {
  return (
    <div>
      <h2 className="mb-4 text-2xl font-bold">
        Patients switching from eculizumab to ULTOMIRIS<sup>&reg;</sup>
      </h2>

      <div className="w-full rounded-xl border border-gray-200 bg-white">
        {/* Header */}
        <div className="flex w-full items-center justify-between rounded-t-xl bg-[#E3F7FF] px-6 py-3">
          <div className="text-sm font-semibold text-gray-800">
            Weight Range {weight}
          </div>
        </div>

        <div className="w-full overflow-x-auto px-4 pb-4 pt-6">
          <svg
            width="802"
            height="120"
            viewBox="0 0 802 120"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g id="Group 9">
              {/* Maintenance Dose Circle */}
              <g id="Frame 87">
                <circle id="Ellipse 3" cx="574" cy="51" r="7" fill="#008578" />
                <g id="Frame 66_Maintenance">
                  <text
                    fill="black"
                    fontFamily="Roboto"
                    fontSize="14"
                    fontWeight="bold"
                  >
                    <tspan x="556.199" y="82.7852">
                      {maintenanceDose.toLocaleString()} ml
                    </tspan>
                  </text>
                  <text fill="black" fontFamily="Roboto" fontSize="12">
                    <tspan x="561.344" y="101.102">
                      {maintenanceDose / 300} VIAL
                      {maintenanceDose / 300 > 1 ? "S" : ""}
                    </tspan>
                  </text>
                </g>
              </g>

              {/* Cycles Info Box */}
              <g id="Frame 86">
                <path d="M801.5 37.5V64.5H615.5V37.5H801.5Z" fill="white" />
                <path d="M801.5 37.5V64.5H615.5V37.5H801.5Z" stroke="#008578" />
                <text
                  fill="#008578"
                  fontFamily="Roboto"
                  fontSize="13"
                  fontStyle="italic"
                  fontWeight="500"
                >
                  <tspan x="625.033" y="54.9434">
                    The same dose for next {cycles} cycles
                  </tspan>
                </text>
              </g>

              {/* Timeline Lines */}
              <path
                d="M194 49C358.411 49.781 450.589 50.219 615 51"
                stroke="url(#paint0_linear_4003_100)"
                strokeWidth="4"
              />
              <line
                x1="32"
                y1="49"
                x2="196"
                y2="49"
                stroke="#FF6A39"
                strokeWidth="4"
              />

              {/* Last Infusion of SOLIRIS Label */}
              <text
                fill="#FF6A39"
                fontFamily="Roboto"
                fontSize="12"
                fontWeight="bold"
              >
                <tspan x="0.378906" y="11.1016">
                  Last Infusion
                </tspan>
                <tspan x="6.33203" y="25.1016">
                  of SOLIRIS
                </tspan>
              </text>

              {/* Loading Dose Label */}
              <text
                fill="#008578"
                fontFamily="Roboto"
                fontSize="12"
                fontWeight="bold"
              >
                <tspan x="174.396" y="11.1016">
                  Loading
                </tspan>
                <tspan x="182.389" y="25.1016">
                  Dose
                </tspan>
              </text>

              {/* Maintenance Dose Label */}
              <text
                fill="#008578"
                fontFamily="Roboto"
                fontSize="12"
                fontWeight="bold"
              >
                <tspan x="354.002" y="11.1016">
                  Maintenance
                </tspan>
                <tspan x="375.389" y="25.1016">
                  Dose
                </tspan>
              </text>

              {/* Loading Dose Value and Vials */}
              <g id="Frame_LoadingDoseValue">
                <text
                  fill="black"
                  fontFamily="Roboto"
                  fontSize="14"
                  fontWeight="bold"
                >
                  <tspan x="175" y="82">
                    {loadingDose.toLocaleString()} ml
                  </tspan>
                </text>
                <text fill="black" fontFamily="Roboto" fontSize="12">
                  <tspan x="180" y="100">
                    {vialNumber} VIAL{vialNumber > 1 ? "S" : ""}
                  </tspan>
                </text>
              </g>

              {/* Maintenance Dose Value (existing one) */}
              <g id="Frame_MaintenanceDoseValue">
                <text
                  fill="black"
                  fontFamily="Roboto"
                  fontSize="14"
                  fontWeight="bold"
                >
                  <tspan x="375.199" y="81.7852">
                    {maintenanceDose.toLocaleString()} ml
                  </tspan>
                </text>
                <text fill="black" fontFamily="Roboto" fontSize="12">
                  <tspan x="380.344" y="100.102">
                    {maintenanceDose / 300} VIAL
                    {maintenanceDose / 300 > 1 ? "S" : ""}
                  </tspan>
                </text>
              </g>

              {/* Marker Rectangles */}
              <rect x="32" y="40" width="4" height="20" rx="2" fill="#FF6A39" />
              <rect
                x="192"
                y="40"
                width="4"
                height="20"
                rx="2"
                fill="#008578"
              />
              <rect
                x="387"
                y="40"
                width="4"
                height="20"
                rx="2"
                fill="#008578"
              />

              {/* Week Frames */}
              <g>
                <path
                  d="M60 49.5C60 43.1487 65.1487 38 71.5 38H156.5C162.851 38 168 43.1487 168 49.5V51.5C168 57.8513 162.851 63 156.5 63H71.5C65.1487 63 60 57.8513 60 51.5V49.5Z"
                  fill="#FFDBCF"
                />
                <text
                  fill="#FF6A39"
                  fontFamily="Roboto"
                  fontSize="13"
                  fontStyle="italic"
                  fontWeight="500"
                >
                  <tspan x="93.4131" y="54.9434">
                    2 Weeks
                  </tspan>
                </text>
              </g>

              <g>
                <path
                  d="M237 49.5C237 43.1487 242.149 38 248.5 38H333.5C339.851 38 345 43.1487 345 49.5V51.5C345 57.8513 339.851 63 333.5 63H248.5C242.149 63 237 57.8513 237 51.5V49.5Z"
                  fill="#A0DAB3"
                />
                <text
                  fill="#008578"
                  fontFamily="Roboto"
                  fontSize="13"
                  fontStyle="italic"
                  fontWeight="500"
                >
                  <tspan x="270.413" y="54.9434">
                    2 Weeks
                  </tspan>
                </text>
              </g>

              <g>
                <path
                  d="M428 50.5C428 44.1487 433.149 39 439.5 39H524.5C530.851 39 536 44.1487 536 50.5V52.5C536 58.8513 530.851 64 524.5 64H439.5C433.149 64 428 58.8513 428 52.5V50.5Z"
                  fill="#A0DAB3"
                />
                <text
                  fill="#008578"
                  fontFamily="Roboto"
                  fontSize="13"
                  fontStyle="italic"
                  fontWeight="500"
                >
                  <tspan x="461.413" y="55.9434">
                    {weeks} Weeks
                  </tspan>
                </text>
              </g>
            </g>

            <defs>
              <linearGradient
                id="paint0_linear_4003_100"
                x1="194"
                y1="53.7978"
                x2="597.982"
                y2="53.7978"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#13756B" />
                <stop offset="1" stopColor="#178F84" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      </div>
    </div>
  );
};
